/**
 * smartFetch - Intelligent fetch wrapper with caching, retries, proxy, and timeout support
 *
 * Features:
 * - Multiple response types (json, text, blob)
 * - Automatic proxy fallback on CORS errors
 * - Retry with exponential backoff
 * - Request timeout handling
 * - Dual caching: Next.js fetch cache + CacheManager
 * - Enhanced error messages with hostname info
 * - Optional callbacks (onSuccess, onError, onComplete)
 * - Debug logging
 *
 * @example
 * // Simple JSON fetch with caching
 * const data = await smartFetch('https://api.example.com/user/123', {
 *   responseType: 'json',
 *   cache: cacheManager,
 *   cacheKey: 'user:123'
 * });
 *
 * @example
 * // With proxy fallback on CORS
 * const data = await smartFetch('https://api.external.com/data', {
 *   responseType: 'json',
 *   proxy: {
 *     url: 'https://proxy.pixelated.tech/',
 *     fallbackOnCors: true
 *   },
 *   retries: 2
 * });
 *
 * @example
 * // Server-side with Next.js caching
 * const data = await smartFetch(url, {
 *   cacheStrategy: 'next',
 *   nextCache: { revalidate: 3600 } // 1 hour
 * });
 */
/**
 * Intelligent fetch with caching, retries, proxy fallback, and timeout
 */
export async function smartFetch(url, options = {}) {
    const { responseType = 'json', proxy, cacheStrategy = typeof window === 'undefined' ? 'next' : 'local', nextCache, cache, cacheKey, retries = 1, timeout = 10000, requestInit = {}, debug = false, onSuccess, onError, onComplete, } = options;
    const hostname = (() => {
        try {
            return new URL(url).hostname;
        }
        catch {
            return 'unknown';
        }
    })();
    try {
        // Step 1: Check CacheManager first (fastest, cross-request)
        if ((cacheStrategy === 'local' || cacheStrategy === 'both') && cache && cacheKey) {
            const cached = cache.get(cacheKey);
            if (cached) {
                if (debug)
                    console.log(`[smartFetch] ${hostname}: Cache hit (${cacheKey})`);
                onSuccess?.(cached);
                onComplete?.();
                return cached;
            }
        }
        // Step 2: Determine fetch URL (direct or via proxy)
        let fetchUrl = url;
        let tryDirect = !proxy?.forceProxy;
        if (proxy?.forceProxy) {
            fetchUrl = proxy.url + encodeURIComponent(url);
            tryDirect = false;
            if (debug)
                console.log(`[smartFetch] ${hostname}: Using proxy (force)`);
        }
        // Step 3: Fetch with retry loop
        let lastError;
        for (let attempt = 0; attempt <= retries; attempt++) {
            try {
                if (debug && attempt > 0) {
                    console.log(`[smartFetch] ${hostname}: Retry attempt ${attempt + 1}/${retries + 1}`);
                }
                // Attempt direct fetch
                if (tryDirect) {
                    try {
                        if (debug)
                            console.log(`[smartFetch] ${hostname}: Fetching (direct)`);
                        // Set up timeout for this fetch attempt
                        const controller = new AbortController();
                        const shouldTimeout = timeout > 0;
                        const timeoutId = shouldTimeout ? setTimeout(() => controller.abort(), timeout) : undefined;
                        // eslint-disable-next-line pixelated/no-direct-fetch
                        const response = await fetch(url, {
                            signal: controller.signal,
                            ...requestInit,
                            ...(cacheStrategy === 'next' || cacheStrategy === 'both'
                                ? { next: nextCache }
                                : {}),
                        });
                        if (timeoutId)
                            clearTimeout(timeoutId);
                        if (!response.ok) {
                            const errorBody = typeof response.text === 'function' ? (await response.text()).trim() : '';
                            throw new Error(errorBody ? `HTTP ${response.status} ${response.statusText}: ${errorBody}` : `HTTP ${response.status} ${response.statusText}`);
                        }
                        // If responseType is 'ok' or 'status', return the raw Response object
                        if (responseType === 'ok' || responseType === 'status') {
                            if (debug)
                                console.log(`[smartFetch] ${hostname}: Success (returning Response object)`);
                            onSuccess?.(response);
                            onComplete?.();
                            return response;
                        }
                        const data = await response[responseType]();
                        // Cache in CacheManager
                        if ((cacheStrategy === 'local' || cacheStrategy === 'both') && cache && cacheKey) {
                            cache.set(cacheKey, data);
                        }
                        if (debug)
                            console.log(`[smartFetch] ${hostname}: Success`);
                        onSuccess?.(data);
                        onComplete?.();
                        return data;
                    }
                    catch (error) {
                        // On CORS error, try proxy if available
                        const corsError = (() => {
                            const message = error.message.toLowerCase();
                            return (message.includes('cors') ||
                                message.includes('cross-origin') ||
                                message.includes('network') ||
                                message.includes('failed to fetch'));
                        })();
                        if (proxy?.fallbackOnCors && corsError) {
                            if (debug)
                                console.log(`[smartFetch] ${hostname}: CORS error, falling back to proxy`);
                            tryDirect = false;
                            fetchUrl = proxy.url + encodeURIComponent(url);
                            // Fall through to proxy attempt below
                        }
                        else {
                            throw error;
                        }
                    }
                }
                if (!tryDirect) {
                    if (debug)
                        console.log(`[smartFetch] ${hostname}: Fetching (proxy)`);
                    // Set up timeout for this fetch attempt
                    const controller = new AbortController();
                    const shouldTimeout = timeout > 0;
                    const timeoutId = shouldTimeout ? setTimeout(() => controller.abort(), timeout) : undefined;
                    // eslint-disable-next-line pixelated/no-direct-fetch
                    const response = await fetch(fetchUrl, {
                        signal: controller.signal,
                        ...requestInit,
                        ...(cacheStrategy === 'next' || cacheStrategy === 'both'
                            ? { next: nextCache }
                            : {}),
                    });
                    if (timeoutId)
                        clearTimeout(timeoutId);
                    if (!response.ok) {
                        const errorBody = typeof response.text === 'function' ? (await response.text()).trim() : '';
                        throw new Error(errorBody ? `HTTP ${response.status} ${response.statusText}: ${errorBody}` : `HTTP ${response.status} ${response.statusText}`);
                    }
                    // If responseType is 'ok' or 'status', return the raw Response object
                    if (responseType === 'ok' || responseType === 'status') {
                        if (debug)
                            console.log(`[smartFetch] ${hostname}: Success (via proxy, returning Response object)`);
                        onSuccess?.(response);
                        onComplete?.();
                        return response;
                    }
                    const data = await response[responseType]();
                    // Cache in CacheManager
                    if ((cacheStrategy === 'local' || cacheStrategy === 'both') && cache && cacheKey) {
                        cache.set(cacheKey, data);
                    }
                    if (debug)
                        console.log(`[smartFetch] ${hostname}: Success (via proxy)`);
                    onSuccess?.(data);
                    onComplete?.();
                    return data;
                }
            }
            catch (error) {
                lastError = error;
                // If we have retries left, wait before retrying
                if (attempt < retries) {
                    const delay = Math.pow(2, attempt) * 100; // Exponential backoff: 100ms, 200ms, 400ms...
                    if (debug)
                        console.log(`[smartFetch] ${hostname}: Waiting ${delay}ms before retry`);
                    await new Promise(resolve => setTimeout(resolve, delay));
                }
            }
        }
        // If we got here, all attempts failed
        const errorMessage = `[smartFetch] ${hostname}: ${lastError?.message || 'Unknown error'}`;
        const error = new Error(errorMessage);
        console.error(errorMessage);
        onError?.(error);
        onComplete?.();
        throw error;
    }
    catch (error) {
        const err = error;
        onError?.(err);
        onComplete?.();
        throw err;
    }
}
